import { useEffect, useState } from 'react';
import { firstNameOf } from './format';

// Read a recommendation aloud with the device's own speech engine.
// Strictly on-device: only voices reporting localService are used, so the text
// is never sent anywhere. If a device has no local voice, the feature hides.

const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;

const LANG_PREFERENCE = ['en-nz', 'en-au', 'en-gb', 'en-us'];

let voice = null;
let speakingId = null;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn());

const pickVoice = () => {
  if (!synth) return null;
  const local = synth.getVoices().filter((v) => v.localService && /^en/i.test(v.lang || ''));
  const normalised = (v) => (v.lang || '').replace('_', '-').toLowerCase();
  for (const lang of LANG_PREFERENCE) {
    const match = local.find((v) => normalised(v) === lang);
    if (match) return match;
  }
  return local[0] || null;
};

const refreshVoice = () => {
  const next = pickVoice();
  // Some engines hand back fresh voice objects each call, so compare identity.
  const identity = (v) => v && (v.voiceURI || v.name);
  if (identity(next) !== identity(voice)) {
    voice = next;
    notify();
  }
};

const clearSpeaking = () => {
  if (speakingId !== null) {
    speakingId = null;
    notify();
  }
};

if (synth) {
  refreshVoice();
  // Chrome populates the voice list asynchronously.
  if (synth.addEventListener) synth.addEventListener('voiceschanged', refreshVoice);
  else synth.onvoiceschanged = refreshVoice;
  // Locking the phone or backgrounding the app leaves iOS speech unrecoverable,
  // so stop rather than trying to resume. Singleton listener, never removed.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') stopSpeech();
  });
}

export function stopSpeech() {
  if (!synth) return;
  synth.cancel();
  clearSpeaking();
}

// Must be called straight from a tap handler: iOS drops speech that is not
// rooted in a user gesture. `onFail` covers the silent-switch/no-audio case.
export function speakRecommendation(id, text, onFail) {
  if (!synth || !voice) return;
  synth.cancel();
  let started = false;
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.voice = voice;
  utterance.lang = voice.lang;
  utterance.rate = 1;
  utterance.onstart = () => {
    started = true;
  };
  utterance.onend = clearSpeaking;
  utterance.onerror = clearSpeaking;
  synth.speak(utterance);
  speakingId = id;
  notify();
  // Nothing audible and nothing queued means the engine refused (a muted device,
  // say), so release the button instead of leaving it stuck on Stop.
  setTimeout(() => {
    if (speakingId === id && !started && !synth.speaking && !synth.pending) {
      clearSpeaking();
      onFail?.();
    }
  }, 800);
}

const spokenCategory = (category) => (category || '').replace(/&/g, 'and');

export const spokenText = (rec, isOwn) => {
  const who = isOwn ? 'You recommend' : `${rec.authorName || 'Someone'} recommends`;
  const parts = [`${who} ${rec.name}.`];
  if (rec.category) parts.push(`Category: ${spokenCategory(rec.category)}.`);
  if (rec.reason) {
    const whose = isOwn ? 'you recommend' : `${firstNameOf(rec.authorName)} recommends`;
    parts.push(`Why ${whose} it: ${rec.reason}`);
  }
  return parts.join(' ');
};

export const useSpeech = (id) => {
  const [, bump] = useState(0);

  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);

  // Leaving the card behind must never leave it talking.
  useEffect(() => () => {
    if (speakingId === id) stopSpeech();
  }, [id]);

  return { supported: Boolean(voice), speaking: speakingId === id };
};
